import {
  ChakraFlex,
  FAIconName,
  FireflyButton,
  FireflyKebabMenu,
  FireflyKebabMenuItem,
  Tag,
  TagColor,
} from '@equip.health/ui';
import { getDischargeStatuses } from './util/dischargePlanUtils';
import { CodeAndLabel } from '~/store/services/clinical-data/refDischargePlan';
import { DischargePlanEvents } from '~/analytics/dischargePlanEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';

export type DischargeModalType = 'start' | 'sign' | 'revise' | 'tasks';

interface DischargePlanHeaderContentProps {
  isDischarged: boolean;
  isStartDischargePlanLoading?: boolean;
  isReadyToDischargeLoading?: boolean;
  status: CodeAndLabel;
  showModal: (modalType: DischargeModalType) => void;
  isStartButtonDisabled: boolean;
}

const DischargePlanHeaderAction = ({
  isDischarged,
  isStartDischargePlanLoading = false,
  isReadyToDischargeLoading = false,
  status,
  isStartButtonDisabled,
  showModal,
}: DischargePlanHeaderContentProps) => {
  const { trackEvent } = useAnalytics();
  const { isPrepping, isSigned } = getDischargeStatuses(status);
  const headerKebabMenuOptions: FireflyKebabMenuItem[] = [
    {
      label: 'Revise discharge plan',
      leftIcon: FAIconName.arrowLeftRotate,
      leftIconColor: 'neutral.secondary',
      onClick: () => {
        trackEvent(
          DischargePlanEvents.clickOnReviseDischargePlanKebabMenuOption,
        );
        showModal('revise');
      },
    },
  ];

  if (isSigned) {
    return (
      <ChakraFlex alignItems="center" flexDirection="row" gap="8px">
        <Tag color={TagColor.green} id="signed-success" value="Signed" />
        {!isDischarged && (
          <FireflyKebabMenu
            aria-label="discharge-plan-header-kebab"
            options={headerKebabMenuOptions}
          />
        )}
      </ChakraFlex>
    );
  }

  // No action buttons for discharged episodes with unsigned plans
  if (isDischarged) {
    return null;
  }

  if (isPrepping) {
    return (
      <FireflyButton
        id="ready-to-discharge-button"
        isLoading={isReadyToDischargeLoading}
        leftIcon={FAIconName.checkRegular}
        onClick={() => {
          trackEvent(DischargePlanEvents.clickOnReadyToDischargeButton);
          showModal('sign');
        }}
      >
        Ready to discharge
      </FireflyButton>
    );
  }

  return (
    <FireflyButton
      id="start-discharge-plan-button"
      isLoading={isStartDischargePlanLoading}
      isDisabled={isStartButtonDisabled}
      leftIcon={FAIconName.plusSolid}
      onClick={() => {
        trackEvent(DischargePlanEvents.clickOnStartDischargePlanButton);
        showModal('start');
      }}
    >
      Start discharge plan
    </FireflyButton>
  );
};

export default DischargePlanHeaderAction;
